import React, { useState } from 'react';
import DocumentSignatureModal from './DocumentSignatureModal';
import { sendForSignature, archiveDocument, deleteDocument } from '../../services/beneficiaryDocumentService';
import './BeneficiaryDocumentViewer.css';

/**
 * Composant pour visualiser un document bénéficiaire en lecture seule
 */
const BeneficiaryDocumentViewer = ({
  db,
  document,
  beneficiaryId,
  onClose,
  onDocumentUpdated,
  allowActions = true,
  isClientView = false
}) => {
  const [showSignatureModal, setShowSignatureModal] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  if (!document) {
    return (
      <div className="beneficiary-document-viewer">
        <div className="no-document">
          <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" width="48" height="48">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
          <p>Aucun document sélectionné</p>
        </div>
      </div>
    );
  }

  const getStatusBadge = (status) => {
    const statusConfig = {
      draft: { label: 'Brouillon', className: 'badge-draft', icon: 'M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z' },
      pending_signature: { label: 'En attente de signature', className: 'badge-pending', icon: 'M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z' },
      signed: { label: 'Signé', className: 'badge-signed', icon: 'M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z' },
      archived: { label: 'Archivé', className: 'badge-archived', icon: 'M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4' }
    };

    const config = statusConfig[status] || statusConfig.draft;

    return (
      <span className={`status-badge ${config.className}`}>
        <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" width="16" height="16">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={config.icon} />
        </svg>
        {config.label}
      </span>
    );
  };

  const handleSendForSignature = async () => {
    try {
      setIsLoading(true);
      await sendForSignature(db, beneficiaryId, document.id);
      alert('Document envoyé pour signature !');
      if (onDocumentUpdated) {
        onDocumentUpdated();
      }
    } catch (error) {
      console.error('Erreur lors de l\'envoi pour signature:', error);
      alert('Erreur: ' + error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleArchive = async () => {
    if (!window.confirm('Êtes-vous sûr de vouloir archiver ce document ?')) {
      return;
    }

    try {
      setIsLoading(true);
      await archiveDocument(db, beneficiaryId, document.id);
      alert('Document archivé !');
      if (onDocumentUpdated) {
        onDocumentUpdated();
      }
    } catch (error) {
      console.error('Erreur lors de l\'archivage:', error);
      alert('Erreur: ' + error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer définitivement ce document ? Cette action est irréversible.')) {
      return;
    }

    try {
      setIsLoading(false);
      await deleteDocument(db, beneficiaryId, document.id);
      alert('Document supprimé !');
      if (onClose) {
        onClose();
      }
    } catch (error) {
      console.error('Erreur lors de la suppression:', error);
      alert('Erreur: ' + error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDownload = () => {
    const blob = new Blob([document.content], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${document.documentName}_${new Date().toISOString().split('T')[0]}.html`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const handlePrint = () => {
    const printWindow = window.open('', '', 'height=800,width=800');
    printWindow.document.write(document.content);
    printWindow.document.close();
    printWindow.print();
  };

  return (
    <div className="beneficiary-document-viewer">
      {/* Header */}
      <div className="viewer-header">
        <div className="header-left">
          <h2>{document.documentName}</h2>
          {getStatusBadge(document.status)}
        </div>
        {onClose && (
          <button onClick={onClose} className="close-button">
            <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" width="20" height="20">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        )}
      </div>

      {/* Document info */}
      <div className="document-info-bar">
        <div className="info-item">
          <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" width="16" height="16">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
          </svg>
          <span>{document.beneficiaryData?.firstName} {document.beneficiaryData?.lastName}</span>
        </div>
        <div className="info-item">
          <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" width="16" height="16">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
          </svg>
          <span>
            Créé le {document.createdAt?.toDate ? document.createdAt.toDate().toLocaleDateString('fr-FR') : 'N/A'}
          </span>
        </div>
      </div>

      {/* Signature info (if signed) */}
      {document.signature && (
        <div className="signature-info-banner">
          <div className="signature-icon">
            <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" width="24" height="24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <div className="signature-details">
            <h4>Document signé électroniquement</h4>
            <p>
              Signé par <strong>{document.signature.signedBy}</strong> le{' '}
              {new Date(document.signature.signedAt).toLocaleString('fr-FR')}
            </p>
            {document.signature.ipAddress && (
              <p className="signature-meta">IP: {document.signature.ipAddress}</p>
            )}
          </div>
          {document.signature.signatureImage && (
            <div className="signature-preview">
              <img src={document.signature.signatureImage} alt="Signature" />
            </div>
          )}
        </div>
      )}

      {/* Action bar */}
      {allowActions && (
        <div className="action-bar">
          <div className="action-buttons">
            {/* Download */}
            <button onClick={handleDownload} className="action-button" title="Télécharger">
              <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" width="18" height="18">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
              </svg>
              Télécharger
            </button>

            {/* Print */}
            <button onClick={handlePrint} className="action-button" title="Imprimer">
              <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" width="18" height="18">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
              </svg>
              Imprimer
            </button>

            {/* Actions according to status */}
            {!isClientView && document.status === 'draft' && (
              <>
                <button
                  onClick={handleSendForSignature}
                  className="action-button primary"
                  disabled={isLoading}
                >
                  <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" width="18" height="18">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                  </svg>
                  Envoyer pour signature
                </button>

                <button
                  onClick={handleDelete}
                  className="action-button danger"
                  disabled={isLoading}
                >
                  <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" width="18" height="18">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                  </svg>
                  Supprimer
                </button>
              </>
            )}

            {document.status === 'pending_signature' && (
              <button
                onClick={() => setShowSignatureModal(true)}
                className="action-button success"
              >
                <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" width="18" height="18">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                </svg>
                Signer maintenant
              </button>
            )}

            {!isClientView && document.status === 'signed' && (
              <button
                onClick={handleArchive}
                className="action-button"
                disabled={isLoading}
              >
                <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" width="18" height="18">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4" />
                </svg>
                Archiver
              </button>
            )}
          </div>
        </div>
      )}

      {/* Document content */}
      <div className="document-content-viewer">
        <div
          className="document-html-content"
          dangerouslySetInnerHTML={{ __html: document.content }}
        />
      </div>

      {/* Signature modal */}
      {showSignatureModal && (
        <DocumentSignatureModal
          db={db}
          document={document}
          beneficiaryId={beneficiaryId}
          onClose={() => setShowSignatureModal(false)}
          onSignatureComplete={() => {
            setShowSignatureModal(false);
            if (onDocumentUpdated) {
              onDocumentUpdated();
            }
          }}
        />
      )}
    </div>
  );
};

export default BeneficiaryDocumentViewer;
